import { assignInlineVars } from '@vanilla-extract/dynamic'
import _merge from 'lodash.merge'
import { Settings } from 'luxon'
import { BookingConfig, Screens, Theme, initApiClient } from 'patient-api-lib'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useOutlet } from 'react-router-dom'
import { appStyleVariants } from './App.css'
import { stickyHeaderClassName } from './constants/app'
import {
  fetchWidgetConfig,
  getStartup,
  selectApiToken,
  selectInitialScreen,
  selectIntegrationType,
  selectLanguage,
  selectMode,
  selectTheme,
  themeSettings,
} from './features/appConfigSlice'
import { selectCurrentScreen } from './features/screensRouterSlice'
import './fonts.css'
import { useAppDispatch } from './hooks/useAppDispatch'
import { useAppSelector } from './hooks/useAppSelector'
import { IntegrationType, MessageType, WidgetMode } from './models/enums'
import { Config, Message, ThemeType, URLTypeSearchParam } from './models/types'
import aquaDentalTheme from './themes/aquaDentalTheme.css'
import defaultTheme from './themes/defaultTheme.css'
import kungstandenTheme from './themes/kungstandenTheme.css'
import themeVars, { theme as blankTheme } from './themes/themeContract.css'
import { convertConfigURLParams } from './util/convertConfigURLParams'
import { getWidgetDimensions } from './util/getDimensions'
import { getExternalHeader } from './util/getExternalHeader'
import { growthbook } from './util/growthbook'
import { rudderAnalytics } from './util/rudderstack'
import { scrollWidget } from './util/scrollWidget'
import { setCustomizableTheme } from './util/setCustomizableTheme'
import {
  checkIsIframe,
  getLocationSearch,
  getSettingsFromWindow,
  getWindow,
} from './util/windowUtils'
import { setJwt } from './features/myBookingsSlice'
import { sendWidgetDisplayed } from './util/messageHandler'
import i18n from './util/i18n'
import * as Sentry from '@sentry/react'

const hostWindow = getWindow()
const isIframe = checkIsIframe()
const locationSearch = getLocationSearch()

const {
  iframeHeight,
  apiToken,
  clinicIds,
  mode,
  treatmentIds,
  enableScrolling,
  initialScreen,
  theme,
  integrationType,
} = getSettingsFromWindow()

export const App = () => {
  const themeState = useAppSelector(selectTheme)
  const language = useAppSelector(selectLanguage)
  const themeConfig = useAppSelector(themeSettings)
  const dispatch = useAppDispatch()
  const outlet = useOutlet()
  const [open, setOpen] = useState(!isIframe)
  const [hostOrigin, setHostOrigin] = useState('')
  const [customTheme, setCustomTheme] = useState<ThemeType | null>()
  const appContainerRef = useRef<HTMLDivElement | null>(null)
  const storeMode = useAppSelector(selectMode)
  const apiTokenStore = useAppSelector(selectApiToken)
  const { height: externalHeaderHeight } = getExternalHeader(
    stickyHeaderClassName
  )
  const initialScreenState = useAppSelector(selectInitialScreen)
  const currentScreen = useAppSelector(selectCurrentScreen)
  const integrationTypeState = useAppSelector(selectIntegrationType)
  const [overridingParams, setOverridingParams] = useState<{
    colors: BookingConfig.Theme.Colors.AsObject
  }>({ colors: {} })
  const URLParams = new URLSearchParams(locationSearch)
  const apiKeyParam = URLParams.get('apiToken')
  const jwtParam = URLParams.get('jwt')
  const bookingId = URLParams.get('bookingId')
  const startApp = useAppSelector(getStartup)
  const integrationTypeURLParam = URLParams.get(
    'integrationType'
  ) as IntegrationType | null

  useEffect(() => {
    setOverridingParams({ colors: themeConfig.colors || {} })
  }, [themeConfig])

  const currentTheme = useMemo<string>(() => {
    if (themeState === Theme.Diplomat) return kungstandenTheme
    if (themeState === Theme.Aristotel) return aquaDentalTheme

    return defaultTheme
  }, [themeState])

  useEffect(() => {
    Settings.defaultZone = 'Europe/Stockholm'
    Settings.defaultLocale = language
    i18n.changeLanguage(language)
  }, [language])

  useEffect(() => {
    // Login for embedded mode
    if ((apiToken && mode === WidgetMode.Embedded) || apiKeyParam) {
      if (!apiToken && !apiKeyParam) console.error('No apiToken provided')

      initApiClient(
        import.meta.env.VITE_PATIENT_API_URL,
        apiKeyParam || apiToken || '',
        import.meta.env.DEV
      )

      const expectedParams: URLTypeSearchParam = {
        theme: 'string',
        language: 'string',
        initialScreen: 'string',
        clinicIds: 'array',
        treatmentIds: 'array',
        mode: 'string',
        integrationType: 'string',
      }

      const reassignConfig: Partial<Config> = {
        ...convertConfigURLParams(URLParams, expectedParams),
        apiToken: apiKeyParam || apiToken || '',
      }

      if (clinicIds && clinicIds.length > 0) {
        reassignConfig.clinicIds = clinicIds
      }

      if (treatmentIds && treatmentIds.length > 0) {
        reassignConfig.treatmentIds = treatmentIds
      }

      if (!checkIsIframe()) {
        reassignConfig.mode = WidgetMode.Modal
      }

      if (mode) {
        reassignConfig.mode = mode as WidgetMode
      }

      if (initialScreen) {
        reassignConfig.initialScreen = initialScreen
      }

      if (theme) {
        reassignConfig.theme = theme
      }

      if (jwtParam) {
        dispatch(setJwt(jwtParam))
      }

      if (bookingId) {
        reassignConfig.bookingId = bookingId
      }

      if (integrationType) {
        reassignConfig.integrationType = integrationType
      }

      dispatch(fetchWidgetConfig(reassignConfig))
      language && i18n.changeLanguage(language)
      themeState && setCustomTheme(_merge(blankTheme, themeState))
      setOpen(true)
    }

    const onMessage = async (event: MessageEvent<Message>) => {
      if (
        (hostOrigin && event.origin !== hostOrigin) ||
        event.source !== getWindow()
      )
        return

      switch (event.data.type) {
        case MessageType.StartMyBooking: {
          if ('data' in event.data) {
            const {
              mode,
              apiToken,
              theme,
              integrationType,
            }: {
              mode?: WidgetMode
              apiToken?: string
              theme?: Theme
              integrationType?: IntegrationType
            } = event.data.data || {}

            if (!apiToken) return

            initApiClient(
              import.meta.env.VITE_PATIENT_API_URL,
              apiToken,
              import.meta.env.DEV
            )

            const expectedParams: URLTypeSearchParam = {
              theme: 'string',
              language: 'string',
              initialScreen: 'string',
              clinicIds: 'array',
              treatmentIds: 'array',
            }

            const reassignConfig = {
              ...convertConfigURLParams(URLParams, expectedParams),
              mode: mode,
              initialScreen: Screens.MyBookingsScreen,
              apiToken,
            }

            if (clinicIds && clinicIds.length > 0) {
              reassignConfig.clinicIds = clinicIds
            }

            if (treatmentIds && treatmentIds.length > 0) {
              reassignConfig.treatmentIds = treatmentIds
            }

            if (theme) {
              reassignConfig.theme = theme
            }

            if (integrationType) {
              reassignConfig.integrationType = integrationType
            }

            await dispatch(fetchWidgetConfig(reassignConfig))
            language && i18n.changeLanguage(language)
            themeState && setCustomTheme(_merge(blankTheme, themeState))
            setHostOrigin(event.origin)
            setOpen(true)
          }
          break
        }
        case MessageType.Start: {
          // Login for modal mode
          if ('data' in event.data) {
            const {
              mode,
              apiToken,
              clinicIds,
              treatmentIds,
              theme,
              integrationType,
            }: {
              mode?: WidgetMode
              apiToken?: string
              clinicIds?: string[]
              treatmentIds?: string[]
              theme?: Theme
              integrationType?: IntegrationType
            } = event.data.data || {}

            if (!apiToken) return

            initApiClient(
              import.meta.env.VITE_PATIENT_API_URL,
              apiToken,
              import.meta.env.DEV
            )

            const expectedParams: URLTypeSearchParam = {
              theme: 'string',
              language: 'string',
              initialScreen: 'string',
              clinicIds: 'array',
              treatmentIds: 'array',
            }

            const reassignConfig = {
              ...convertConfigURLParams(URLParams, expectedParams),
              mode: mode,
              apiToken,
            }

            if (clinicIds && clinicIds.length > 0) {
              reassignConfig.clinicIds = clinicIds
            }

            if (treatmentIds && treatmentIds.length > 0) {
              reassignConfig.treatmentIds = treatmentIds
            }

            if (mode) {
              reassignConfig.mode
            }

            if (theme) {
              reassignConfig.theme = theme
            }

            if (integrationType) {
              reassignConfig.integrationType = integrationType
            }

            dispatch(fetchWidgetConfig(reassignConfig))
            language && i18n.changeLanguage(language)
            themeState && setCustomTheme(_merge(blankTheme, themeState))
            setHostOrigin(event.origin)
            setOpen(true)
          }
          break
        }
      }
    }

    window.addEventListener('message', onMessage, false)

    hostWindow?.postMessage({ type: MessageType.Ready }, '*')

    return () => {
      window.removeEventListener('message', onMessage, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  useEffect(() => {
    // Scroll to the top of the widget when the screen changes
    if (mode !== WidgetMode.Embedded || !enableScrolling) return

    if (appContainerRef.current) {
      if (currentScreen && currentScreen !== initialScreenState) {
        const { height: externalHeaderHeight } = getExternalHeader(
          stickyHeaderClassName
        )
        scrollWidget(appContainerRef.current, externalHeaderHeight)
      }
    }
  }, [appContainerRef, currentScreen, initialScreenState])

  const calculateWidgetDimensions = useMemo(
    () => getWidgetDimensions(iframeHeight, integrationTypeURLParam),
    [integrationTypeURLParam]
  )

  useEffect(() => {
    growthbook.setAttributes({
      id: rudderAnalytics.getAnonymousId(),
      company: apiTokenStore,
    })
  }, [apiTokenStore])

  const appClassName = useMemo(() => {
    return `${storeMode ? appStyleVariants[storeMode] : ''} ${currentTheme}`
  }, [currentTheme, storeMode])

  useEffect(() => {
    rudderAnalytics.ready(() => {
      const anonymousId = rudderAnalytics.getAnonymousId()

      if (anonymousId && !startApp) {
        rudderAnalytics.identify(anonymousId, {
          lang: i18n.language,
          mode: storeMode,
          integrationType: integrationTypeState,
        })
        sendWidgetDisplayed()
        Sentry.setContext('character', {
          anonymousId: anonymousId,
          lang: i18n.language,
          mode: storeMode,
          integrationType: integrationTypeState,
        })
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startApp])

  return (
    <div
      ref={appContainerRef}
      className={appClassName}
      style={
        customTheme
          ? assignInlineVars(themeVars, {
              ...setCustomizableTheme(customTheme, overridingParams),
              externalHeader: {
                height: externalHeaderHeight
                  ? `${externalHeaderHeight}px`
                  : '0px',
              },
              widgetDimensions: calculateWidgetDimensions,
            })
          : {}
      }
    >
      {open && outlet}
    </div>
  )
}
